import type { Dispatch, SetStateAction } from "react";
import type { Asset, ReferenceItem, Session, SourceTab } from "../../api/types.js";
import { createLinkAsset, uploadAsset as uploadAssetRequest } from "./assetApi.js";
import { setWorkFavorite } from "../works/workApi.js";

export type UseAssetsState = {
  saveLinkAsset: () => Promise<void>;
  uploadAsset: (file: File) => Promise<void>;
  saveSourceAsAsset: (source: ReferenceItem) => Promise<void>;
  attachRecent: (title: string) => void;
};

export function useAssets({
  currentProjectId,
  currentSession,
  setAssets,
  setCitationSources,
  setReferencePool,
  setPrompt,
  setPlusOpen,
  setDrawerTab,
  setDrawerOpen
}: {
  currentProjectId: string;
  currentSession: Session | null;
  setAssets: Dispatch<SetStateAction<Asset[]>>;
  setCitationSources: Dispatch<SetStateAction<ReferenceItem[]>>;
  setReferencePool: Dispatch<SetStateAction<ReferenceItem[]>>;
  setPrompt: Dispatch<SetStateAction<string>>;
  setPlusOpen: Dispatch<SetStateAction<boolean>>;
  setDrawerTab: Dispatch<SetStateAction<SourceTab>>;
  setDrawerOpen: Dispatch<SetStateAction<boolean>>;
}): UseAssetsState {
  async function saveLinkAsset() {
    if (!currentProjectId) return;
    const url = window.prompt("资料链接 URL");
    if (!url) return;
    const title = window.prompt("资料标题") || url;
    const asset = await createLinkAsset({
      projectId: currentProjectId,
      sessionId: currentSession?.id,
      title,
      url,
      kind: "link"
    });
    setAssets((old) => [asset, ...old]);
    setDrawerTab("assets");
    setDrawerOpen(true);
    setPlusOpen(false);
  }

  async function uploadAsset(file: File) {
    if (!currentProjectId) return;
    const asset = await uploadAssetRequest({
      projectId: currentProjectId,
      sessionId: currentSession?.id,
      file
    });
    setAssets((old) => [asset, ...old]);
    setDrawerTab("assets");
    setDrawerOpen(true);
    setPlusOpen(false);
  }

  async function saveSourceAsAsset(source: ReferenceItem) {
    if (!source.workId) return;
    const isSaved = !source.isSaved;
    const favorite = await setWorkFavorite(source.workId, isSaved, isSaved ? currentSession?.id : undefined);
    const updateSource = (item: ReferenceItem) => item.workId === source.workId
      ? { ...item, isSaved, isSessionBound: favorite.sessionId ? true : item.isSessionBound }
      : item;
    setCitationSources((old) => old.map(updateSource));
    setReferencePool((old) => old.map(updateSource));
    setAssets((old) => {
      const favoriteId = `favorite:${source.workId}`;
      if (!isSaved) return old.filter((asset) => asset.id !== favoriteId);
      if (old.some((asset) => asset.id === favoriteId)) return old;
      return [{
        id: favoriteId,
        workId: source.workId,
        title: source.title,
        kind: "paper",
        url: source.url,
        meta: { authors: source.authors, year: source.year, venue: source.venue, isSaved: true },
        createdAt: new Date().toISOString()
      }, ...old];
    });
  }

  function attachRecent(title: string) {
    setPrompt((old) => old ? `${old}\n\n请结合资料：${title}` : `请结合资料：${title}`);
    setPlusOpen(false);
  }

  return {
    saveLinkAsset,
    uploadAsset,
    saveSourceAsAsset,
    attachRecent
  };
}
